/**
 * DraftStep -> preview-entity builder lookup.
 *
 * **Track 2 Phase 3 deliverable** (per `docs/plans/2026-05-08_track2_drafting_unification.md`).
 *
 * ## Why this exists
 *
 * Phase 1 introduced `DRAFT_STEP_REGISTRY` as the single source of truth
 * for per-step metadata (prompt key, accepts-point-input, text-input
 * reference, etc.). Phase 2 migrated `buildDraftPrompt` from a 210-case
 * switch to registry-driven dispatch.
 *
 * Phase 3 (this file) takes the same approach for preview entities: the
 * 67-case `buildDraftPreviewEntity` switch in
 * `draftingHelpers.ts:875` is replaced with a `Partial<Record<DraftStep, ...>>`
 * lookup table. Each entry is a pure function `(session, ctx) -> EntityPreview | null`.
 *
 * ## Design
 *
 * Keys present in this table: only the DraftStep variants for which the
 * legacy switch produced a preview entity (67 of 223 — the remaining
 * 156 always returned `null`, which is now the missing-key default).
 *
 * The `Partial<Record<DraftStep, BuildPreviewFn>>` typing intentionally
 * does **not** force exhaustive coverage — pure-fallthrough steps (e.g.
 * `line-first-point`, `polyline-width-start`, all the `arraypolar-*`
 * steps that explicitly skip preview) carry no entry. This mirrors the
 * legacy switch's `default: return null` branch byte-for-byte.
 *
 * ## CLAUDE.md fidelity
 *
 * Per ПРИОРИТЕТ #1 (нет упрощений) — every case body from the legacy
 * switch is reproduced verbatim, including:
 *  - `circumscribingCircleFromThree` for CIRCLE-3P (no polyline approximation)
 *  - `generatePolygonVertices` for POLYGON (full inscribed/circumscribed math)
 *  - midpoint construction for CIRCLE-2P (faithful AutoCAD parity)
 *  - `distanceBetween` for ELLIPSE minor distance
 *
 * No defaults, no wildcards. The fall-through (`null`) branch is the
 * only behavioural shortcut, and only for steps the legacy switch
 * already excluded.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { t } from "../../i18n";
import {
  buildArcPreviewEntity,
  buildArrayRectDraftPreview,
  buildBlockPreviewEntity,
  buildCirclePreviewEntity,
  buildDimensionPreviewEntity,
  buildEllipsePreviewEntity,
  buildHatchPreviewEntity,
  buildLinePreviewEntity,
  buildMTextPreviewEntity,
  buildMultileaderPreviewEntity,
  buildPolylinePreviewEntity,
  buildRectanglePreviewEntity,
  buildRevisionCloudPathPreviewEntity,
  buildRevisionCloudPreviewEntity,
  buildViewportPreviewEntity,
  buildWipeoutPreviewEntity,
} from "../draftPreviewEntities";
// REVCLOUD-MODES — session arc length feeds the rubber-band previews.
import { revcloudSessionArcLength } from "./revcloudDraft";
import {
  circumscribingCircleFromThree,
  generatePolygonVertices,
} from "../draftSharedHelpers";
import { distanceBetween } from "../draftTypes";
import type { DraftSession, DraftStep } from "../draftTypes";

/**
 * Context threaded through every preview builder. Carries the per-call
 * data (preview entity id, default object properties) that the legacy
 * switch computed once at the top of `buildDraftPreviewEntity` and then
 * reused in each case branch.
 */
export interface DraftPreviewBuildContext {
  readonly previewId: string;
  readonly defaults: Record<string, string>;
}

/**
 * Pure function: (session, ctx) -> preview entity (or null when the
 * session lacks the data this step needs to render — e.g. waiting for
 * `hoverPoint`).
 */
export type DraftPreviewBuildFn = (
  session: DraftSession,
  ctx: DraftPreviewBuildContext,
) => EntityPreview | null;

/**
 * Map of DraftStep -> preview builder.
 *
 * Order mirrors the legacy switch in `draftingHelpers.ts` (which in
 * turn followed the source-of-truth `DraftStep` union ordering) for
 * review-friendliness.
 */
export const DRAFT_PREVIEW_BUILDERS: Readonly<
  Partial<Record<DraftStep, DraftPreviewBuildFn>>
> = {
  // ── LINE ────────────────────────────────────────────────────────────
  "line-next-point": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points.at(-1) ?? session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,

  // ── CIRCLE ──────────────────────────────────────────────────────────
  "circle-radius": (session, ctx) =>
    session.hoverPoint
      ? buildCirclePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_circle"),
        )
      : null,
  "circle-ready": (session, ctx) =>
    buildCirclePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_circle"),
    ),
  "circle-2p-second-point": (session, ctx) => {
    // CIRCLE 2P preview: center = midpoint of (p1, hover); radius =
    // distance(p1, hover) / 2 — AutoCAD's 2-point construction. We
    // synthesise a virtual "center" + "radius point" pair for the
    // existing builder, which expects (center, point-on-circle).
    if (!session.hoverPoint) return null;
    const midX = (session.points[0].x + session.hoverPoint.x) / 2;
    const midY = (session.points[0].y + session.hoverPoint.y) / 2;
    const midPoint = { x: midX, y: midY };
    return buildCirclePreviewEntity(
      ctx.previewId,
      session.spaceId,
      midPoint,
      session.hoverPoint,
      ctx.defaults,
      t("preview.draft_circle"),
    );
  },
  "circle-3p-second-point": (session, ctx) =>
    // CIRCLE 3P at second point — show a chord preview from p1 to
    // the hover, like ARC's second-point chord.
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "circle-3p-third-point": (session, ctx) => {
    // CIRCLE 3P at third point — compute the circumscribing circle
    // through (p1, p2, hover). If the three points are collinear we
    // fall back to no preview.
    if (!session.hoverPoint) return null;
    const fit = circumscribingCircleFromThree(
      session.points[0],
      session.points[1],
      session.hoverPoint,
    );
    if (!fit) return null;
    return buildCirclePreviewEntity(
      ctx.previewId,
      session.spaceId,
      fit.center,
      { x: fit.center.x + fit.radius, y: fit.center.y },
      ctx.defaults,
      t("preview.draft_circle"),
    );
  },

  // ── DONUT ───────────────────────────────────────────────────────────
  // At the center step, show the outer circle preview at the hover
  // point so the user sees where the donut will land. AutoCAD shows a
  // filled annulus rubber-band at hover; we approximate with an
  // outline-only circle of the typed outer radius (the painted band
  // width is encoded by the LWPOLYLINE per-segment width on commit, not
  // the preview).
  "donut-center": (session, ctx) => {
    if (!session.hoverPoint) return null;
    const outerD = Number(session.options.donutOuterD ?? "1.0");
    if (!Number.isFinite(outerD) || outerD <= 0) return null;
    const outerR = outerD / 2;
    return buildCirclePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.hoverPoint,
      { x: session.hoverPoint.x + outerR, y: session.hoverPoint.y },
      ctx.defaults,
      t("preview.draft_donut"),
    );
  },

  // ── ARC ─────────────────────────────────────────────────────────────
  "arc-second": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.arc_chord"),
        )
      : null,
  "arc-end": (session, ctx) =>
    session.hoverPoint
      ? buildArcPreviewEntity(
          ctx.previewId,
          session.spaceId,
          [...session.points, session.hoverPoint],
          ctx.defaults,
          t("preview.draft_arc"),
        )
      : null,
  "arc-ready": (session, ctx) =>
    buildArcPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points,
      ctx.defaults,
      t("preview.draft_arc"),
    ),
  "arc-sce-center": (session, ctx) =>
    // ARC Start-Center-End at the center step — show a radius
    // indicator line from start to the hover (which becomes the
    // center).
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,

  // ── ELLIPSE rubber-band previews (Phase D4) ─────────────────────────
  "ellipse-major-end": (session, ctx) =>
    // Show a tracker line from center → hover so the user can see the
    // major-axis direction + length while picking the second click.
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "ellipse-minor-distance": (session, ctx) => {
    // After major endpoint is fixed, show the actual ellipse with
    // ratio derived from |center → hover| (the typed minor distance).
    if (!session.hoverPoint) return null;
    return buildEllipsePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      distanceBetween(session.points[0], session.hoverPoint),
      ctx.defaults,
      t("preview.draft_ellipse"),
    );
  },

  // ── POLYLINE ────────────────────────────────────────────────────────
  "polyline-next-point": (session, ctx) =>
    session.points.length >= 2 || session.hoverPoint
      ? buildPolylinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          [
            ...session.points,
            ...(session.hoverPoint ? [session.hoverPoint] : []),
          ],
          session.closed,
          ctx.defaults,
          t("preview.draft_polyline"),
        )
      : null,

  // ── SPLINE rubber-band preview ──────────────────────────────────────
  // Show a polyline approximation through the accumulated fit-points
  // and the hover point. Once committed, the geometry/create-spline
  // reducer rebuilds the proper NURBS curve (the SplineRenderer paints
  // the cubic Bezier segments). The preview uses a polyline because
  // the rubber-band UX only needs to suggest the path the user is
  // sketching — exact NURBS interpolation is expensive and unnecessary
  // for visual feedback.
  "spline-fit-next": (session, ctx) =>
    session.points.length >= 1 || session.hoverPoint
      ? buildPolylinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          [
            ...session.points,
            ...(session.hoverPoint ? [session.hoverPoint] : []),
          ],
          false,
          ctx.defaults,
          t("preview.draft_spline"),
        )
      : null,

  // ── RECTANGLE ───────────────────────────────────────────────────────
  "rectangle-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildRectanglePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_rectangle"),
        )
      : null,
  "rectangle-ready": (session, ctx) =>
    buildRectanglePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_rectangle"),
    ),

  // ── POLYGON ─────────────────────────────────────────────────────────
  "polygon-radius": (session, ctx) => {
    // Hover defines the radius; build a closed N-sided polygon
    // preview centred on session.points[0] with radius = distance to
    // hover. Inscribed/circumscribed mode is read from session.options.
    const polyCenter = session.points[0];
    if (!polyCenter || !session.hoverPoint) return null;
    const polyRadius = distanceBetween(polyCenter, session.hoverPoint);
    if (!Number.isFinite(polyRadius) || polyRadius <= 0) return null;
    const polySides = Math.max(
      3,
      Math.min(
        1024,
        Math.floor(Number(session.options.polygonSides ?? "4")) || 4,
      ),
    );
    const polyMode: "inscribed" | "circumscribed" =
      session.options.polygonMode === "circumscribed"
        ? "circumscribed"
        : "inscribed";
    const polyVertices = generatePolygonVertices(
      polyCenter,
      polySides,
      polyRadius,
      polyMode,
    );
    return buildPolylinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      // Repeat first vertex so the closed boundary renders cleanly in
      // the polyline preview (matches RECTANG preview pattern).
      [...polyVertices, polyVertices[0] ?? polyCenter],
      true,
      ctx.defaults,
      t("preview.draft_polyline"),
    );
  },

  // ── MTEXT ───────────────────────────────────────────────────────────
  "mtext-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildMTextPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          "MTEXT",
          ctx.defaults,
          t("preview.draft_mtext"),
        )
      : null,

  // ── BLOCK / INSERT ──────────────────────────────────────────────────
  "block-insertion-point": (session, ctx) =>
    session.hoverPoint
      ? buildBlockPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_insert"),
          session.options.blockName ?? "BLOCK_REF",
        )
      : null,
  "block-scale": (session, ctx) =>
    buildBlockPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0] ?? { x: 0, y: 0 },
      ctx.defaults,
      t("preview.draft_insert"),
      session.options.blockName ?? "BLOCK_REF",
      session.options.blockScale ?? "1",
      session.options.blockRotation ?? "0",
    ),
  "block-rotation": (session, ctx) =>
    buildBlockPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0] ?? { x: 0, y: 0 },
      ctx.defaults,
      t("preview.draft_insert"),
      session.options.blockName ?? "BLOCK_REF",
      session.options.blockScale ?? "1",
      session.options.blockRotation ?? "0",
    ),
  "block-ready": (session, ctx) =>
    buildBlockPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      ctx.defaults,
      t("preview.draft_block"),
      session.options.blockName ?? "BLOCK_REF",
    ),

  // ── ARRAYRECT ───────────────────────────────────────────────────────
  // User is dragging to define opposite corner — auto-calc rows/cols
  // from the delta for preview; committed on Enter.
  "arrayrect-count": (session, ctx) =>
    session.hoverPoint && session.points[0]
      ? buildArrayRectDraftPreview(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_arrayrect"),
          session.options,
        )
      : null,
  // Parameters are locked from the previous step; hover no longer
  // changes grid geometry.
  "arrayrect-spacing": (session, ctx) =>
    session.points[0]
      ? buildArrayRectDraftPreview(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.points[1] ?? session.points[0],
          ctx.defaults,
          t("preview.draft_arrayrect"),
          session.options,
        )
      : null,

  // ── HATCH ───────────────────────────────────────────────────────────
  // hatch-pattern / hatch-mode-prompt / hatch-pick-internal-point /
  // hatch-pick-additional intentionally have no preview: internal-pick
  // mode does not show a live preview boundary — the user is hovering
  // over potentially complex geometry and a probe-on-hover boundary
  // trace would either flicker or pin the UI to the worker. AutoCAD's
  // preview here is a small crosshair under the cursor, which the
  // canvas already shows via the standard hover indicator.
  "hatch-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildHatchPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_hatch"),
          {
            pattern: session.options.hatchPattern,
            scale: session.options.hatchScale,
          },
        )
      : null,
  "hatch-ready": (session, ctx) =>
    buildHatchPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_hatch"),
      {
        pattern: session.options.hatchPattern,
        scale: session.options.hatchScale,
      },
    ),

  // ── DIMENSION (linear / aligned / angular shared families) ──────────
  "dimension-second-point": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension_baseline"),
        )
      : null,
  "dimaligned-second-point": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension_baseline"),
        )
      : null,
  "dimangular-second-line": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension_baseline"),
        )
      : null,
  "dimension-line-point": (session, ctx) =>
    session.hoverPoint
      ? buildDimensionPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.points[1],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension"),
        )
      : null,
  "dimaligned-line-point": (session, ctx) =>
    session.hoverPoint
      ? buildDimensionPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.points[1],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension"),
        )
      : null,
  "dimangular-arc-point": (session, ctx) =>
    session.hoverPoint
      ? buildDimensionPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.points[1],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_dimension"),
        )
      : null,
  "dimension-ready": (session, ctx) =>
    buildDimensionPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1],
      session.points[2] ?? session.points[1],
      ctx.defaults,
      t("preview.draft_dimension"),
    ),
  "dimradius-dim-line": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          session.command === "DIMRADIUS"
            ? t("preview.draft_radius_dim")
            : t("preview.draft_diameter_dim"),
        )
      : null,
  "dimdiameter-dim-line": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          session.command === "DIMRADIUS"
            ? t("preview.draft_radius_dim")
            : t("preview.draft_diameter_dim"),
        )
      : null,
  "dimordinate-leader-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_ordinate_dimension"),
        )
      : null,

  // ── MLEADER ─────────────────────────────────────────────────────────
  "mleader-landing-point": (session, ctx) =>
    session.hoverPoint
      ? buildMultileaderPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          "MLEADER",
          ctx.defaults,
          t("preview.draft_leader"),
        )
      : null,

  // ── REVCLOUD ────────────────────────────────────────────────────────
  "revcloud-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildRevisionCloudPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_revcloud"),
          revcloudSessionArcLength(session),
        )
      : null,
  "revcloud-ready": (session, ctx) =>
    buildRevisionCloudPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_revcloud"),
      revcloudSessionArcLength(session),
    ),
  // REVCLOUD-MODES — Polygonal rubber-band: picked vertices + the live
  // hover point; Freehand: the sampled cursor path.
  "revcloud-polygonal-next": (session, ctx) => {
    const pts = session.hoverPoint
      ? [...session.points, session.hoverPoint]
      : session.points;
    if (pts.length < 2) return null;
    return buildRevisionCloudPathPreviewEntity(
      ctx.previewId,
      session.spaceId,
      pts,
      ctx.defaults,
      t("preview.draft_revcloud"),
      revcloudSessionArcLength(session),
    );
  },
  "revcloud-freehand-path": (session, ctx) => {
    if (session.points.length < 2) return null;
    return buildRevisionCloudPathPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points,
      ctx.defaults,
      t("preview.draft_revcloud"),
      revcloudSessionArcLength(session),
    );
  },

  // ── WIPEOUT ─────────────────────────────────────────────────────────
  "wipeout-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildWipeoutPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_wipeout"),
        )
      : null,
  "wipeout-ready": (session, ctx) =>
    buildWipeoutPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_wipeout"),
    ),

  // ── VIEWPORT ────────────────────────────────────────────────────────
  "viewport-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildViewportPreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_viewport"),
        )
      : null,
  "viewport-ready": (session, ctx) =>
    buildViewportPreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_viewport"),
    ),

  // ── VPCLIP-INTERACTIVE ──────────────────────────────────────────────
  // AutoCAD parity: the rubber band for a rectangular clip boundary pick
  // renders as a plain dashed boundary outline (like a WINDOW-select /
  // RECTANG rubber band) — NOT a "Viewport" frame glyph, since this is a
  // clip boundary on an EXISTING viewport, not a new viewport being
  // created. Reuses `buildRectanglePreviewEntity` verbatim (the same
  // helper RECTANG's own `rectangle-second-corner` uses) rather than
  // `buildViewportPreviewEntity`, whose "Display lock"/"Scale"/"Shade
  // plot" properties would be meaningless for a transient boundary
  // preview.
  "vpclip-second-corner": (session, ctx) =>
    session.hoverPoint
      ? buildRectanglePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_vpclip"),
        )
      : null,

  // ── BLOCK AUTHORING — Linear parameter placement preview (Phase K.2a) ─
  // While picking the end point we show the linear-axis line from the
  // base to the hover point so the user can see the axis they're
  // about to commit. While picking the label we show the committed
  // base->end line plus a hint line to the label location, so the user
  // can position the label without losing visual context.
  "param-linear-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-linear-label": (session, ctx) =>
    // The base->end line is now committed-shape; we re-render it as a
    // preview line so the canvas gives continuous feedback while the
    // user picks the label position. Phase K.2a renders the simple
    // line preview; the dedicated label-glyph layer lives in the
    // BlockAuthoringRenderer (rendered when activeSpace is BEDIT and
    // the parameter has been added to the dynamicMetadata).
    buildLinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_line"),
    ),

  // ── BLOCK AUTHORING — multi-click parameter placement (Phase K.2b) ──
  // Each multi-click flow shows an axis-line preview between the
  // previously-clicked point and the hover, just like Linear.
  "param-flip-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-polar-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-xy-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-rotation-radius-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-alignment-end": (session, ctx) =>
    session.hoverPoint
      ? buildLinePreviewEntity(
          ctx.previewId,
          session.spaceId,
          session.points[0],
          session.hoverPoint,
          ctx.defaults,
          t("preview.draft_line"),
        )
      : null,
  "param-flip-label": (session, ctx) =>
    buildLinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_line"),
    ),
  "param-polar-label": (session, ctx) =>
    buildLinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_line"),
    ),
  "param-xy-label": (session, ctx) =>
    buildLinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_line"),
    ),
  "param-rotation-label": (session, ctx) =>
    buildLinePreviewEntity(
      ctx.previewId,
      session.spaceId,
      session.points[0],
      session.points[1] ?? session.points[0],
      ctx.defaults,
      t("preview.draft_line"),
    ),
};

/**
 * Lookup helper. Mirrors the legacy `default: return null` branch by
 * returning `null` when `step` has no entry (e.g. selection /
 * dialog steps without rubber-band visualisation).
 */
export function buildDraftPreviewEntityFromRegistry(
  session: DraftSession,
  ctx: DraftPreviewBuildContext,
): EntityPreview | null {
  const fn = DRAFT_PREVIEW_BUILDERS[session.step];
  return fn ? fn(session, ctx) : null;
}
